import { MutationCache, QueryClient } from '@tanstack/react-query';
import { toast } from '@/components/orno/toast';
import { i18n } from '@/i18n';

import { isApiError } from './errors';

function shouldRetry(failureCount: number, error: unknown): boolean {
  // Client errors (400, 404, 409) do not get better by retrying.
  if (isApiError(error) && error.status >= 400 && error.status < 500) return false;
  return failureCount < 2;
}

/** The message of a failed call, for a toast or an inline error. */
export function errorMessage(error: unknown): string {
  if (isApiError(error)) {
    if (error.status === 0) return i18n.t('common:errors.network');
    if (error.status === 503 && error.code === 'capacity') return i18n.t('common:errors.capacity');
    if (error.status === 400 && error.code === 'too-many-chargers' && error.limit !== undefined) {
      return i18n.t('chargers:limit.tooMany', { limit: error.limit });
    }
    if (error.status === 400) return i18n.t('common:errors.badRequest');
    if (error.status === 409) return i18n.t('common:errors.conflict');
  }
  return i18n.t('common:errors.generic');
}

declare module '@tanstack/react-query' {
  interface Register {
    mutationMeta: {
      /**
       * The mutation's form says why it failed, under the form or at its fields: the global toast stays silent, so a
       * failure is announced once.
       */
      inlineError?: boolean;
    };
  }
}

/**
 * The TanStack Query client with the app defaults: mutation errors are toasted globally (except a 401, which signs in
 * again, and a mutation whose form shows its own failure, `meta: { inlineError: true }`), query errors are rendered
 * inline. Live data comes over the event stream, so queries do not poll.
 */
export function createQueryClient(options: { retry?: boolean } = {}): QueryClient {
  return new QueryClient({
    mutationCache: new MutationCache({
      onError: (error, _variables, _context, mutation) => {
        // A 401 has already started the sign-in redirect (the session middleware); a toast would only flash.
        if (isApiError(error) && error.status === 401) return;
        if (mutation.meta?.inlineError === true) return;
        toast.error(errorMessage(error));
      },
    }),
    defaultOptions: {
      queries: {
        staleTime: 5_000,
        gcTime: 5 * 60_000,
        retry: options.retry === false ? false : shouldRetry,
        refetchOnWindowFocus: true,
      },
      mutations: {
        retry: false,
      },
    },
  });
}
